import {
  CustomAction,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  FormDialogMode,
  RowCustomAction,
  SortDirection,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { CloudDownload, DateRange, ShowChart, Tune, ViewModule, Warning } from '@material-ui/icons'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumEstadoFichero, EnumTipoDocumento } from '../../../../api'
import { DocumentModel } from '../../../../api/Argentia/endpoints/documents/model/document'
import { enumToSelectOptionArray } from '../../../../helpers'
import { fileDownloadHelper } from '../../../../helpers/fileDownloadHelper'
import { missingFieldsFormWithRequiredText } from '../../../../helpers/validateFormHelper'
import { FormDialogDefinitionProps, useFormDialog } from '../../../../hooks'
import { downloadCurrentBADFile } from '../../../../pages/documents/documents.service'
import { getCustomerDomain } from '../../../../redux'
import { CardActionProps } from '../../cardActions/cardActions'
import { ButtonOption } from '../../splitButton/splitButton'
import { StandardCard } from '../../standardCard/standardCard'
import {
  mapDocumentModelToDataTable,
  mapDownloadCurrentFormDataToRequest
} from './historicalFilesCard.mapper'
import {
  DownloadCurrentFormData,
  historicalFilesCardDataTableModel,
  HistoricalFilesSplitButtonOptions
} from './historicalFilesCard.model'

interface HistoricalFilesCardProps {
  data: DocumentModel[]
  isLoading?: boolean
  onDocumentAddClick?: () => void
  onConsumptionByProfileClick?: () => void
  onConsumptionByCurveClick?: () => void
  onConsumptionByPeriodClick?: () => void
  onConsumptionWithProfileSelectionClick?: () => void
  onDownloadSeasonsTemplateClick?: () => void
  onDownloadGenericTemplateClick?: () => void
  onDownloadActionHandler?: (document: DocumentModel) => void
  onViewObjectionsHandler?: (document: DocumentModel) => void
  showDownloadCurrentButton?: boolean
  supplyPointId?: number // required for download current button
}

export const HistoricalFilesCard: React.FC<HistoricalFilesCardProps> = ({
  data,
  isLoading,
  onDocumentAddClick,
  onConsumptionByProfileClick,
  onConsumptionByCurveClick,
  onConsumptionByPeriodClick,
  onConsumptionWithProfileSelectionClick,
  onDownloadSeasonsTemplateClick,
  onDownloadGenericTemplateClick,
  onDownloadActionHandler,
  onViewObjectionsHandler,
  showDownloadCurrentButton = true,
  supplyPointId
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'profiles'])
  const { openForm, updateFormDefinition, closeForm, formProps, CustomFormDialog, setFormWaiting } =
    useFormDialog()
  const { info, error } = useNotify()
  const [invalidDate, setInvalidDate] = useState<boolean>(false)

  const noData = data && data.length === 0
  const dataTableData: historicalFilesCardDataTableModel[] = mapDocumentModelToDataTable(data) ?? []
  const preventStandardButton = !isLoading && noData && !onDocumentAddClick

  // #region Handlers Starts
  const handleInsert = async row => {
    onDocumentAddClick && onDocumentAddClick()
    return true
  }

  const handleDownloadCurrent = () => {
    openForm({
      definition: downloadCurrentFormDefinition,
      title: t('profiles:components.historicalFilesCard.downloadCurrentFormDialog.title'),
      subtitle: t('profiles:components.historicalFilesCard.downloadCurrentFormDialog.subtitle'),
      mode: FormDialogMode.EDIT,
      inputData: {
        documentType: EnumTipoDocumento.BAD,
        supplyPointId: supplyPointId
      },
      isWaiting: false,
      onClose: onDownloadCurrentFormCloseHandler
    })
  }

  const onDownloadCurrentFormCloseHandler = async (formData: DownloadCurrentFormData) => {
    if (formData) {
      if (missingFieldsFormWithRequiredText(downloadCurrentFormDefinition?.sections!, formData)) {
        error(
          t(
            'profiles:components.historicalFilesCard.downloadCurrentFormDialog.errors.incompleteForm'
          )
        )
        return
      }
      const request = mapDownloadCurrentFormDataToRequest(formData)
      await downloadCurrentBADFile(request).then(response => {
        if (response.success && response.data) {
          fileDownloadHelper(response.data.name, response.data)
        } else {
          error(
            t(
              'profiles:components.historicalFilesCard.downloadCurrentFormDialog.errors.downloadError'
            )
          )
        }
      })
    }
    closeForm()
  }
  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts
  const downloadCurrentFormDefinition = React.useMemo<FormDialogDefinitionProps>(
    () => ({
      id: 'EDIT',
      sections: [
        {
          inputs: [
            {
              name: 'documentType',
              defaultValue: EnumTipoDocumento.BAD,
              display: false
            },
            {
              name: 'supplyPointId',
              display: false
            },
            {
              name: 'startDate',
              label: t(
                'profiles:components.historicalFilesCard.downloadCurrentFormDialog.inputs.startDate'
              ),
              type: DataType.DATE,
              errorState: invalidDate,
              errorText: t(
                'profiles:components.historicalFilesCard.downloadCurrentFormDialog.errors.startDateError'
              ),
              required: true,
              onChange: (value, formState: any) => {
                if (moment(value).isAfter(moment(formState.endDate))) {
                  setInvalidDate(true)
                } else {
                  setInvalidDate(false)
                }
              }
            },
            {
              name: 'endDate',
              label: t(
                'profiles:components.historicalFilesCard.downloadCurrentFormDialog.inputs.endDate'
              ),
              type: DataType.DATE,
              errorState: invalidDate,
              errorText: t(
                'profiles:components.historicalFilesCard.downloadCurrentFormDialog.errors.endDateError'
              ),
              required: true,
              onChange: (value, formState: any) => {
                if (moment(value).isBefore(moment(formState.startDate))) {
                  setInvalidDate(true)
                } else {
                  setInvalidDate(false)
                }
              }
            }
          ]
        }
      ]
    }),
    [invalidDate, t]
  )

  // #endregion Form Definition Ends

  // #region DataTable Config Starts

  const getCustomRowActions = () => {
    let customActions: RowCustomAction[] = []
    if (onDownloadActionHandler) {
      customActions.push({
        icon: <CloudDownload fontSize="small" />,
        tooltip: t('profiles:components.historicalFilesCard.dataTable.tooltips.downloadFile'),
        onClick: onDownloadActionHandler,
        userPermission: UserPermission.EDIT,
        visibleIf: 'canDownloadDocument'
      })
    }
    if (onViewObjectionsHandler) {
      customActions.push({
        icon: <Warning fontSize="small" />,
        tooltip: t('profiles:components.historicalFilesCard.dataTable.tooltips.viewObjections'),
        onClick: onViewObjectionsHandler,
        userPermission: UserPermission.EDIT
      })
    }
    return customActions
  }

  const getFabCustomActions = () => {
    let fabCustomActions: CustomAction[] = []

    if (onConsumptionByCurveClick) {
      fabCustomActions.push({
        icon: <ShowChart fontSize="small" />,
        tooltip: t(
          'profiles:components.historicalFilesCard.dataTable.customFabActions.consumptionByCurve'
        ),
        onClick: onConsumptionByCurveClick,
        userPermission: UserPermission.EDIT
      })
    }

    if (onConsumptionByProfileClick) {
      fabCustomActions.push({
        icon: <ViewModule fontSize="small" />,
        tooltip: t(
          'profiles:components.historicalFilesCard.dataTable.customFabActions.consumptionByProfile'
        ),
        onClick: onConsumptionByProfileClick,
        userPermission: UserPermission.EDIT
      })
    }

    if (onConsumptionByPeriodClick) {
      fabCustomActions.push({
        icon: <DateRange fontSize="small" />,
        tooltip: t(
          'profiles:components.historicalFilesCard.dataTable.customFabActions.consumptionByPeriod'
        ),
        onClick: onConsumptionByPeriodClick,
        userPermission: UserPermission.EDIT
      })
    }

    if (onDownloadSeasonsTemplateClick) {
      fabCustomActions.push({
        icon: <ViewModule fontSize="small" />,
        tooltip: t(
          'profiles:components.historicalFilesCard.dataTable.customFabActions.downloadSeasonsTemplate'
        ),
        onClick: onDownloadSeasonsTemplateClick,
        userPermission: UserPermission.EDIT
      })
    }

    if (onConsumptionWithProfileSelectionClick) {
      fabCustomActions.push({
        icon: <Tune fontSize="small" />,
        tooltip: t(
          'profiles:components.historicalFilesCard.dataTable.customFabActions.consumptionWithProfileSelection'
        ),
        onClick: onConsumptionWithProfileSelectionClick,
        userPermission: UserPermission.EDIT
      })
    }

    if (onDownloadGenericTemplateClick) {
      fabCustomActions.push({
        icon: <ShowChart fontSize="small" />,
        tooltip: t(
          'profiles:components.historicalFilesCard.dataTable.customFabActions.downloadStandardTemplate'
        ),
        onClick: onDownloadGenericTemplateClick,
        userPermission: UserPermission.EDIT
      })
    }

    return fabCustomActions.length > 0 ? fabCustomActions : undefined
  }

  const documentDataTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 0,
    filterType: FilterType.ADVANCED,
    download: true,
    canEdit: false,
    canDelete: false,
    canInsert: true,
    customInsertEditModal: true,

    pagination: true,

    analytics: {
      customer: getCustomerDomain(),
      view: t('profiles:components.historicalFilesCard.dataTable.analytics')
    },
    onRowInsert: handleInsert,
    rowCustomActions: getCustomRowActions(),
    fabCustomActions: getFabCustomActions(),
    sortOrder: {
      name: 'documentDate',
      direction: SortDirection.DESC
    },
    customSort: (data, colIndex, order) => {
      const dtIndex = documentDataTableColumns.findIndex(c => c.name === 'documentDate')

      return data.sort((a, b) => {
        const valA = a.data[colIndex]
        const valB = b.data[colIndex]

        if (colIndex === dtIndex) {
          const timeA = valA ? moment(valA, 'DD-MM-YYYY HH:mm').valueOf() : 0
          const timeB = valB ? moment(valB, 'DD-MM-YYYY HH:mm').valueOf() : 0

          return (timeA - timeB) * (order === 'asc' ? 1 : -1)
        }

        if (valA < valB) return order === 'asc' ? -1 : 1
        if (valA > valB) return order === 'asc' ? 1 : -1
        return 0
      })
    }
  }

  const documentDataTableColumns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML
    },
    {
      name: 'documentDate',
      label: t('profiles:components.historicalFilesCard.dataTable.columns.documentDate'),
      type: DataType.TEXT
    },
    {
      name: 'type',
      label: t('profiles:components.historicalFilesCard.dataTable.columns.type'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumTipoDocumento, 'enums:EnumTipoDocumento')
    },
    {
      name: 'description',
      label: t('profiles:components.historicalFilesCard.dataTable.columns.description'),
      type: DataType.TEXT
    },
    {
      name: 'state',
      label: t('profiles:components.historicalFilesCard.dataTable.columns.state'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumEstadoFichero, 'enums:EnumEstadoFichero')
    }
  ]

  // #endregion DataTable Config Ends

  // #region Hooks Starts
  useEffect(() => {
    updateFormDefinition(downloadCurrentFormDefinition)
  }, [downloadCurrentFormDefinition])

  // #endregion Hooks Ends

  // #region Footer Actions Starts
  const footerActions: CardActionProps[] = []
  showDownloadCurrentButton &&
    supplyPointId &&
    footerActions.push({
      label: t('profiles:components.historicalFilesCard.actions.downloadCurrent'),
      handleActionClick: handleDownloadCurrent,
      hasPermission: true,
      color: 'secondary'
    })
  // #endregion Footer Actions Ends

  // #region Split Button Options Starts

  const getSplitButtonOptions = () => {
    let options: ButtonOption[] = []

    if (onConsumptionByCurveClick) {
      options.push({
        id: 0,
        name: HistoricalFilesSplitButtonOptions.CURVE,
        label: t('profiles:components.historicalFilesCard.splitButton.curve')
      })
    }

    if (onConsumptionByProfileClick) {
      options.push({
        id: 1,
        name: HistoricalFilesSplitButtonOptions.PROFILE,
        label: t('profiles:components.historicalFilesCard.splitButton.profile')
      })
    }

    if (onConsumptionByPeriodClick) {
      options.push({
        id: 2,
        name: HistoricalFilesSplitButtonOptions.PERIOD,
        label: t('profiles:components.historicalFilesCard.splitButton.period')
      })
    }

    if (onDownloadGenericTemplateClick) {
      options.push({
        id: 3,
        name: HistoricalFilesSplitButtonOptions.GENERIC_TEMPLATE,
        label: t('profiles:components.historicalFilesCard.splitButton.standard')
      })
    }

    if (onDownloadSeasonsTemplateClick) {
      options.push({
        id: 4,
        name: HistoricalFilesSplitButtonOptions.SEASONS_TEMPLATE,
        label: t('profiles:components.historicalFilesCard.splitButton.seasons')
      })
    }

    if (onConsumptionWithProfileSelectionClick) {
      options.push({
        id: 5,
        name: HistoricalFilesSplitButtonOptions.CONSUMPTION_WITH_PROFILE_SELECTION,
        label: t('profiles:components.historicalFilesCard.splitButton.profileSelection')
      })
    }

    return options
  }

  const handleSplitButtonClick = (option: ButtonOption) => {
    switch (option.name) {
      case HistoricalFilesSplitButtonOptions.PROFILE:
        onConsumptionByProfileClick && onConsumptionByProfileClick()
        break
      case HistoricalFilesSplitButtonOptions.CURVE:
        onConsumptionByCurveClick && onConsumptionByCurveClick()
        break
      case HistoricalFilesSplitButtonOptions.PERIOD:
        onConsumptionByPeriodClick && onConsumptionByPeriodClick()
        break
      case HistoricalFilesSplitButtonOptions.SEASONS_TEMPLATE:
        onDownloadSeasonsTemplateClick && onDownloadSeasonsTemplateClick()
        break
      case HistoricalFilesSplitButtonOptions.CONSUMPTION_WITH_PROFILE_SELECTION:
        onConsumptionWithProfileSelectionClick && onConsumptionWithProfileSelectionClick()
        break
      case HistoricalFilesSplitButtonOptions.GENERIC_TEMPLATE:
        onDownloadGenericTemplateClick && onDownloadGenericTemplateClick()
        break
      default:
        break
    }
  }
  // #endregion Split Button Options Ends

  return (
    <>
      <StandardCard
        title={t('profiles:components.historicalFilesCard.title')}
        isLoading={isLoading}
        showEmptyContent={noData}
        emptyCaption={t('profiles:components.historicalFilesCard.emptyCaption')}
        emptyButtonCallback={preventStandardButton ? undefined : onDocumentAddClick}
        emptyButtonCaption={t('profiles:components.historicalFilesCard.mainAction.add')}
        footerActions={noData || footerActions.length === 0 ? undefined : footerActions}
        splitButtonOptions={preventStandardButton ? getSplitButtonOptions() : undefined}
        onSplitButtonClick={preventStandardButton ? handleSplitButtonClick : undefined}
      >
        <DataTable
          data={dataTableData}
          columns={documentDataTableColumns}
          options={documentDataTableOptions}
        />
      </StandardCard>

      <CustomFormDialog {...formProps} />
    </>
  )
}
